import React, { useEffect, useState } from "react";
import { Box, Grid2 } from "@mui/material";
import Button from "../../base/Button";
import { useDispatch, useSelector } from "../../../store";
import { useNavigate } from "react-router-dom";
import { createTile } from "../../../slices/tile";
import { useForm, SubmitHandler } from "react-hook-form";
import { useTileOptions } from "../../../hooks/useTileOptions";
import BasicInfo from "./BasicInfoForm";
import AlertsForm from "./AlertsForm";
import GraphInfo from "./GraphInfoForm";
import FilterForm from "./FilterForm";
import NotificationForm from "./NotificationForm";
import Tile from "../../../types/tile";
import Connection from "../../../types/connection";
import DataType from "../../../types/dataType";
import TileType, { TileTypes } from "../../../types/tileType";
import { useSkeletonLoader } from "../../../hooks/useSkeletonLoader";
import AlertDelivery, {
  AlertDeliveryTypes,
} from "../../../types/alertDelivery";
import NotificationSchedule from "../../../types/notificationSchedule";
import { getDashboardDetail } from "../../../slices/dashboard";

interface Props {
  tile?: Tile;
  dashboardId: number;
  tileType: "dashboard" | "tile";
}

const TileForm: React.FC<Props> = (props) => {
  const { tile, dashboardId, tileType } = props;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const dashboardOptions = useSelector(
    (store) => store.dashboard.dashboardOptions,
  );
  const connectionTypes = useSelector(
    (store) => store.connection.connectionTypes,
  );
  const backgrounds = useSelector((store) => store.tiles.backgrounds);
  const icons = useSelector((store) => store.tiles.icons);
  const emailGroups = useSelector((store) => store.emails.emailGroups);
  const scheduledNotifications = useSelector(
    (store) => store.schedules.scheduledNotifications,
  );
  const filters = useSelector((store) => store.filter.filters);
  const filterStatus = useSelector((store) => store.filter.status);
  const status = useSelector((store) => store.tiles.tileSaveStatus);
  const settings = useSelector((store) => store.settings.settings);
  const incidentTypes = useSelector((store) => store.settings.incidentTypes);
  const jobTypes = useSelector((store) => store.settings.jobTypes);

  const [enabledConnectionTypes, setEnabledConnectionTypes] =
    useState(connectionTypes);
  const [connectionList, setConnectionList] = useState<Connection[]>();
  const [dataTypeList, setDataTypeList] = useState<DataType[]>();
  const [tileTypeList, setTileTypeList] = useState<TileType[]>();

  const addSubtypesToDeliveries = (deliveries: AlertDelivery[] = []) => {
    const newList = deliveries.map((d) => ({
      ...d,
      methodSubtype:
        d.methodSubtype ??
        (d.deliveryMethod.id === AlertDeliveryTypes.SvPro
          ? d.gmdJobType?.id
            ? AlertDeliveryTypes.Gmd
            : d.responderIncidentType?.id
              ? AlertDeliveryTypes.Responder
              : d.investigatorCaseType?.id
                ? AlertDeliveryTypes.Investigator
                : AlertDeliveryTypes.SvPro
          : undefined),
    }));

    return newList;
  };

  const {
    handleSubmit,
    control,
    watch,
    reset,
    resetField,
    setValue,
    register,
    formState,
    trigger,
  } = useForm<Tile>({
    mode: "onChange",
    defaultValues: {
      ...tile,
      dashboard: dashboardOptions
        ? dashboardOptions.find((x) => x.id === dashboardId)
        : undefined,
      dataType:
        tileType === "dashboard"
          ? dataTypeList?.find((x) => x.id === 5)
          : undefined,
      type:
        tileType === "dashboard"
          ? tileTypeList?.find((x) => x.id === TileTypes.Dashboard)
          : undefined,
      alerts: {
        offline: {
          ...tile?.alerts?.offline,
          alertDeliveries: addSubtypesToDeliveries(
            tile?.alerts?.offline?.alertDeliveries || [],
          ),
          alertType: { id: 1, value: "Offline" },
        },
        threshold: {
          ...tile?.alerts?.threshold,
          alertDeliveries: addSubtypesToDeliveries(
            tile?.alerts?.threshold?.alertDeliveries || [],
          ),
          alertType: { id: 2, value: "Threshold" },
        },
      },
      scheduledReport: tile?.scheduledReport || {
        enabled: false,
        notificationSchedules: [] as NotificationSchedule[],
      },
    },
  });

  const formValues = watch();

  const watchOfflineEnabled = watch("alerts.offline.enabled");
  const watchThresholdEnabled = watch("alerts.threshold.enabled");
  const watchReportGroups = watch("scheduledReport.notificationSchedules");

  register("alerts.threshold.deliveryRequired", {
    validate: () =>
      !watchThresholdEnabled ||
      formValues.alerts?.threshold?.alertDeliveries?.length! > 0 ||
      "Delivery Method Required to Send Threshold Alerts",
  });

  register("alerts.offline.deliveryRequired", {
    validate: () =>
      !watchOfflineEnabled ||
      formValues.alerts?.offline?.alertDeliveries?.length! > 0 ||
      "Delivery Method Required to Send Offline Alerts",
  });

  const onSubmit: SubmitHandler<Tile> = (data) => {
    if (data.alerts?.offline && !data.alerts?.offline?.enabled)
      data.alerts.offline = undefined;
    if (data.alerts?.threshold && !data.alerts?.threshold?.enabled)
      data.alerts.threshold = undefined;

    dispatch(createTile(data)).then((result) => {
      if (!!result && result.meta.requestStatus === "fulfilled") {
        reset();
        dispatch(getDashboardDetail(formValues.dashboard?.id!));
        if (tileType === "tile") {
          navigate(`/tile/${result.payload}`);
        } else {
          navigate(`/dashboard/${formValues.dashboard?.id}`);
        }
      }
    });
  };

  const handleCancel = () => {
    reset();
    navigate(`/dashboard/${dashboardId ?? formValues.dashboard?.id}`);
  };

  const { loadWithSkeleton } = useSkeletonLoader(
    !dashboardOptions ||
      (tileType === "tile" &&
        (!connectionTypes ||
          !emailGroups ||
          !scheduledNotifications ||
          (tile?.id! > 0 &&
            (!dataTypeList || !tileTypeList || filterStatus === "loading")))),
  );

  useTileOptions({
    formValues,
    resetField,
    dispatch,
    setConnectionList,
    setDataTypeList,
    setTileTypeList,
    connectionTypes,
    tileType,
    svAlertsEnabled:
      settings?.notificationSettings?.svNotifications &&
      (watchOfflineEnabled || watchThresholdEnabled),
    filters,
    setValue,
    watchOfflineEnabled,
    watchThresholdEnabled,
    watchReportGroups,
    dashboardOptions,
    dashboardId,
    settings,
    setEnabledConnectionTypes,
    dataTypeList,
    tileTypeList,
  });

  // Fill in form with existing tile values
  useEffect(() => {
    if (!!tile) {
      reset({
        ...tile,
        alerts: {
          offline: {
            ...tile.alerts?.offline,
            alertDeliveries: addSubtypesToDeliveries(
              tile.alerts?.offline?.alertDeliveries || [],
            ),
          },
          threshold: {
            ...tile.alerts?.threshold,
            alertDeliveries: addSubtypesToDeliveries(
              tile.alerts?.threshold?.alertDeliveries || [],
            ),
          },
        },
      });
    }
  }, [reset, tile]);

  return (
    <Box display="flex" flexDirection="column" width="100%">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid2 container spacing={2}>
          <BasicInfo
            tileType={tileType}
            control={control}
            dashboardOptions={dashboardOptions}
            connectionTypes={enabledConnectionTypes}
            connectionList={connectionList}
            formValues={formValues}
            dataTypeList={dataTypeList}
            tileTypeList={tileTypeList}
            backgrounds={backgrounds}
            icons={icons}
            loadWithSkeleton={loadWithSkeleton}
            setValue={setValue}
          />
          {tileType === "tile" && (
            <GraphInfo
              control={control}
              formValues={formValues}
              loadWithSkeleton={loadWithSkeleton}
            />
          )}
          {tileType === "tile" && !!formValues.type?.hasFilters && (
            <FilterForm
              control={control}
              formValues={formValues}
              filters={filters}
              filterLoading={filterStatus === "loading"}
              setValue={setValue}
              loadWithSkeleton={loadWithSkeleton}
            />
          )}
          {tileType === "tile" && settings && (
            <AlertsForm
              control={control}
              formValues={formValues}
              settings={settings}
              loadWithSkeleton={loadWithSkeleton}
            />
          )}
          {tileType === "tile" &&
            (settings?.notificationSettings?.emailNotifications ||
              settings?.notificationSettings?.siteNotifications ||
              settings?.notificationSettings?.svNotifications) && (
              <NotificationForm
                setValue={setValue}
                control={control}
                formValues={formValues}
                emailGroups={emailGroups}
                scheduledNotifications={scheduledNotifications}
                notificationSettings={settings.notificationSettings}
                loadWithSkeleton={loadWithSkeleton}
                enabledNotifications={{
                  threshold: watchThresholdEnabled,
                  offline: watchOfflineEnabled,
                }}
                responderIncidentTypes={incidentTypes}
                gmdJobTypes={jobTypes}
                error={formState.errors}
                trigger={trigger}
              />
            )}
          <Grid2 size={{ xs: 12 }}>
            <Box
              margin={2}
              display="flex"
              flexDirection="row"
              justifyContent="flex-end"
              gap={2}
              color="text.secondary"
            >
              <Button
                label="Cancel"
                type="button"
                onClick={handleCancel}
                cancel
              />
              <Button
                variant="contained"
                label="Save"
                type="submit"
                onClick={() => {}}
                color="primary"
                loading={status === "loading"}
              />
            </Box>
          </Grid2>
        </Grid2>
      </form>
    </Box>
  );
};

export default TileForm;
